import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  Building2,
  ChevronLeft,
  Cpu,
  Filter,
  Layers3,
  Search,
  Stethoscope,
  Wrench,
} from "lucide-react";

import {
  fetchCategories,
  fetchFaults,
  scoreFault,
  searchTerms,
  normalize,
  fetchPlcManufacturers,
  fetchPlcManufacturer,
  fetchPlcSeries,
  fetchPlcSeriesById,
  fetchPlcModels,
  fetchPlcFaults,
  fetchPlcDiagnosticCodes,
} from "@/faults-app/lib/db";

import {
  AppShell,
  StatusBadge,
  severityToneKey,
} from "@/faults-app/components/app-shell";

import { faultIcon } from "@/faults-app/components/electric-icons";

type SearchParams = {
  cat: string | undefined;
  q: string;
  plcBrand: string | undefined;
  plcSeries: string | undefined;
  plcModel: string | undefined;
};

export const Route = createFileRoute("/library")({
  validateSearch: (
    s: Record<string, unknown>,
  ): SearchParams => ({
    cat:
      typeof s["cat"] === "string"
        ? s["cat"]
        : undefined,
    q:
      typeof s["q"] === "string"
        ? s["q"]
        : "",
    plcBrand:
      typeof s["plcBrand"] === "string"
        ? s["plcBrand"]
        : undefined,
    plcSeries:
      typeof s["plcSeries"] === "string"
        ? s["plcSeries"]
        : undefined,
    plcModel:
      typeof s["plcModel"] === "string"
        ? s["plcModel"]
        : undefined,
  }),

  head: () => ({
    meta: [
      {
        title: "مكتبة الأعطال الكهربائية",
      },
      {
        name: "description",
        content:
          "مكتبة شاملة للأعطال الكهربائية بالعربي: الأعراض والأسباب والاختبارات والقراءات الطبيعية والحلول.",
      },
      {
        property: "og:title",
        content: "مكتبة الأعطال الكهربائية",
      },
      {
        property: "og:description",
        content:
          "تصفح وابحث في مكتبة الأعطال الكهربائية باللغة العربية.",
      },
    ],
  }),

  component: Library,
});

function Library() {
  const {
    cat,
    q,
    plcBrand,
    plcSeries,
    plcModel,
  } = Route.useSearch();

  const navigate = Route.useNavigate();

  const {
    data: categories = [],
  } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const {
    data: faults = [],
  } = useQuery({
    queryKey: ["faults"],
    queryFn: () => fetchFaults(),
  });

  const category = categories.find(
    (c) => c.slug === cat,
  );

  const isPlcCategory =
    !!category &&
    (
      category.slug
        .toLowerCase()
        .includes("plc") ||
      category.name_ar
        .toLowerCase()
        .includes("plc")
    );

  const terms = searchTerms(q ?? "");

  const list = faults
    .filter((f) =>
      category
        ? f.category_id === category.id
        : true,
    )
    .filter((f) =>
      terms.length
        ? scoreFault(f, terms) > 0
        : true,
    );

  const resetPlcSearch = () => ({
    plcBrand: undefined,
    plcSeries: undefined,
    plcModel: undefined,
  });

  return (
    <AppShell>
      <main className="mx-auto w-full max-w-5xl px-4 pt-6">
        <header className="rounded-3xl panel-dark p-5 shadow-card">
          <h1 className="flex items-center gap-2 text-xl font-extrabold text-white sm:text-2xl">
            <BookOpen className="h-6 w-6 text-primary" />
            {category
              ? `مكتبة أعطال: ${category.name_ar}`
              : "مكتبة الأعطال الكهربائية"}
          </h1>

          <div className="relative mt-4">
            <Search className="pointer-events-none absolute end-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />

            <input
              value={q ?? ""}
              onChange={(e) =>
                navigate({
                  search: {
                    cat,
                    q: e.target.value,
                    plcBrand,
                    plcSeries,
                    plcModel,
                  },
                })
              }
              placeholder={
                isPlcCategory
                  ? "ابحث عن شركة PLC أو سلسلة أو موديل أو عطل أو كود..."
                  : "ابحث عن جهاز أو عطل أو عرض أو كود خطأ..."
              }
              className="w-full rounded-xl border border-input bg-card p-3 pe-11 text-base outline-none transition focus:border-primary focus:ring-3 focus:ring-primary/25"
            />
          </div>
        </header>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 text-xs font-bold text-muted-foreground">
            <Filter className="h-4 w-4" />
            تصفية
          </span>

          <Link
            to="/library"
            search={{
              cat: undefined,
              q: q ?? "",
              ...resetPlcSearch(),
            }}
            className={`press rounded-full border px-3 py-1.5 text-sm font-bold transition ${
              !cat
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-card hover:border-primary"
            }`}
          >
            الكل
          </Link>

          {categories.map((c) => (
            <Link
              key={c.id}
              to="/library"
              search={{
                cat: c.slug,
                q: q ?? "",
                ...resetPlcSearch(),
              }}
              className={`press rounded-full border px-3 py-1.5 text-sm font-bold transition ${
                c.slug === cat
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card hover:border-primary"
              }`}
            >
              {c.name_ar}
            </Link>
          ))}
        </div>

        {isPlcCategory ? (
          <PlcLibrary
            cat={cat}
            q={q}
            plcBrand={plcBrand}
            plcSeries={plcSeries}
            plcModel={plcModel}
            navigate={navigate}
          />
        ) : (
          <GenericFaultList list={list} />
        )}
      </main>
    </AppShell>
  );
}

function GenericFaultList({
  list,
}: {
  list: Awaited<
    ReturnType<typeof fetchFaults>
  >;
}) {
  return (
    <ul className="mt-5 grid gap-3 lg:grid-cols-2">
      {list.map((f) => {
        const Ico = faultIcon(
          `${f.name_ar} ${f.device_ar}`,
        );

        return (
          <li
            key={f.id}
            className="card-tech p-4"
          >
            <div className="flex items-start gap-3">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-accent text-accent-foreground">
                <Ico className="h-6 w-6" />
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-base font-extrabold">
                    {f.name_ar}
                  </h2>

                  {f.error_code && (
                    <span className="rounded bg-steel px-2 py-0.5 text-xs font-bold text-steel-foreground">
                      {f.error_code}
                    </span>
                  )}
                </div>

                <p className="mt-0.5 text-xs text-muted-foreground">
                  {f.device_ar}
                </p>

                <ul className="mt-2 space-y-1 text-sm">
                  {f.symptoms
                    .slice(0, 2)
                    .map((s, i) => (
                      <li
                        key={i}
                        className="flex gap-2 text-muted-foreground"
                      >
                        <span className="text-primary-dark">
                          •
                        </span>
                        <span className="line-clamp-1">
                          {s}
                        </span>
                      </li>
                    ))}
                </ul>

                <div className="mt-3">
                  <StatusBadge
                    tone={severityToneKey(
                      f.severity,
                    )}
                  >
                    درجة الخطورة: {f.severity}
                  </StatusBadge>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              {f.next_question_key ? (
                <Link
                  to="/diagnose"
                  search={{
                    node: f.next_question_key,
                  }}
                  className="press inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-3 py-2.5 text-sm font-extrabold text-primary-foreground transition hover:bg-primary-dark hover:text-white"
                >
                  <Stethoscope className="h-4 w-4" />
                  ابدأ التشخيص
                </Link>
              ) : (
                <span className="inline-flex items-center justify-center rounded-xl bg-muted px-3 py-2.5 text-sm font-bold text-muted-foreground">
                  لا يوجد مسار تفاعلي
                </span>
              )}

              <Link
                to="/fault/$id"
                params={{
                  id: f.id,
                }}
                className="press inline-flex items-center justify-center rounded-xl border border-steel/30 bg-card px-3 py-2.5 text-sm font-extrabold transition hover:border-primary"
              >
                عرض التفاصيل
              </Link>
            </div>
          </li>
        );
      })}

      {list.length === 0 && (
        <li className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          لا توجد أعطال مطابقة لبحثك.
        </li>
      )}
    </ul>
  );
}

type PlcNavigate = ReturnType<
  typeof Route.useNavigate
>;

function PlcLibrary({
  cat,
  q,
  plcBrand,
  plcSeries,
  plcModel,
  navigate,
}: {
  cat: string | undefined;
  q: string;
  plcBrand: string | undefined;
  plcSeries: string | undefined;
  plcModel: string | undefined;
  navigate: PlcNavigate;
}) {
  const manufacturers =
    fetchPlcManufacturers();

  const manufacturer =
    plcBrand
      ? fetchPlcManufacturer(plcBrand)
      : null;

  const series =
    plcBrand && plcSeries
      ? fetchPlcSeriesById(
          plcBrand,
          plcSeries,
        )
      : null;

  const normalizedQuery = normalize(
    q.trim(),
  );

  const queryMatches = (
    values: string[],
  ) => {
    if (!normalizedQuery) {
      return true;
    }

    const words = normalizedQuery
      .split(/[\s،.,؟?/_-]+/)
      .filter(Boolean);

    const hay = normalize(
      values.join(" "),
    );

    return words.every((word) =>
      hay.includes(word),
    );
  };

  const go = (
    next: Partial<SearchParams>,
  ) =>
    navigate({
      search: {
        cat,
        q,
        plcBrand,
        plcSeries,
        plcModel,
        ...next,
      },
    });

  return (
    <section className="mt-5">
      <PlcBreadcrumb
        manufacturerName={
          manufacturer?.name
        }
        seriesName={series?.name}
        model={plcModel}
        onRoot={() =>
          go({
            plcBrand: undefined,
            plcSeries: undefined,
            plcModel: undefined,
          })
        }
        onManufacturer={() =>
          go({
            plcSeries: undefined,
            plcModel: undefined,
          })
        }
        onSeries={() =>
          go({
            plcModel: undefined,
          })
        }
      />

      {!plcBrand && (
        <ManufacturerGrid
          manufacturers={manufacturers.filter(
            (item) =>
              queryMatches([
                item.id,
                item.name,
                ...(item.aliases ?? []),
              ]),
          )}
          onSelect={(id) =>
            go({
              plcBrand: id,
              plcSeries: undefined,
              plcModel: undefined,
            })
          }
        />
      )}

      {plcBrand &&
        manufacturer &&
        !plcSeries && (
          <SeriesGrid
            manufacturerId={
              manufacturer.id
            }
            series={fetchPlcSeries(
              manufacturer.id,
            ).filter((item) =>
              queryMatches([
                item.id,
                item.name,
                ...(item.aliases ?? []),
                ...(item.engineeringSoftware ?? []),
                ...(item.models ?? []),
              ]),
            )}
            onSelect={(id) =>
              go({
                plcSeries: id,
                plcModel: undefined,
              })
            }
          />
        )}

      {plcBrand &&
        plcSeries &&
        series &&
        !plcModel && (
          <ModelGrid
            manufacturerId={plcBrand}
            seriesId={plcSeries}
            models={fetchPlcModels(
              plcBrand,
              plcSeries,
            ).filter((model) =>
              queryMatches([model]),
            )}
            onSelect={(model) =>
              go({
                plcModel: model,
              })
            }
          />
        )}

      {plcBrand &&
        plcSeries &&
        plcModel && (
          <PlcModelDetails
            manufacturerId={plcBrand}
            seriesId={plcSeries}
            model={plcModel}
            q={q}
          />
        )}

      {plcBrand &&
        !manufacturer && (
          <EmptyPlcState
            text="الشركة المحددة غير موجودة بالمكتبة."
          />
        )}

      {plcBrand &&
        plcSeries &&
        !series && (
          <EmptyPlcState
            text="السلسلة المحددة غير موجودة بالمكتبة."
          />
        )}
    </section>
  );
}

function PlcBreadcrumb({
  manufacturerName,
  seriesName,
  model,
  onRoot,
  onManufacturer,
  onSeries,
}: {
  manufacturerName?: string;
  seriesName?: string;
  model?: string;
  onRoot: () => void;
  onManufacturer: () => void;
  onSeries: () => void;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-card p-3 text-sm">
      <button
        type="button"
        onClick={onRoot}
        className="font-extrabold text-primary hover:underline"
      >
        شركات PLC
      </button>

      {manufacturerName && (
        <>
          <ChevronLeft className="h-4 w-4 text-muted-foreground" />
          <button
            type="button"
            onClick={onManufacturer}
            className="font-bold hover:text-primary"
          >
            {manufacturerName}
          </button>
        </>
      )}

      {seriesName && (
        <>
          <ChevronLeft className="h-4 w-4 text-muted-foreground" />
          <button
            type="button"
            onClick={onSeries}
            className="font-bold hover:text-primary"
          >
            {seriesName}
          </button>
        </>
      )}

      {model && (
        <>
          <ChevronLeft className="h-4 w-4 text-muted-foreground" />
          <span className="font-extrabold">
            {model}
          </span>
        </>
      )}
    </div>
  );
}


const plcCompanyLogos: Record<string, string> = {
  siemens: "/faults-app/brand-logos/vfd/siemens.png",
  rockwell: "/faults-app/brand-logos/vfd/allen-bradley.png",
  mitsubishi: "/faults-app/brand-logos/vfd/mitsubishi-electric.png",
  schneider: "/faults-app/brand-logos/vfd/schneider-electric.png",
  omron: "/faults-app/brand-logos/vfd/omron.png",
  abb: "/faults-app/brand-logos/vfd/abb.png",
  delta: "/faults-app/brand-logos/vfd/delta.png",
  beckhoff: "/faults-app/brand-logos/vfd/beckhoff.png",
  wago: "/faults-app/brand-logos/vfd/wago.png",
  "phoenix-contact": "/faults-app/brand-logos/vfd/phoenix-contact.png",
  unitronics: "/faults-app/brand-logos/vfd/unitronics.png",
  keyence: "/faults-app/brand-logos/vfd/keyence.png",
  panasonic: "/faults-app/brand-logos/vfd/panasonic.png",
  "ls-electric": "/faults-app/brand-logos/vfd/ls-electric.png",
  automationdirect: "/faults-app/brand-logos/vfd/automationdirect.png",
  "br-automation": "/faults-app/brand-logos/vfd/br-automation.png",
  "bosch-rexroth": "/faults-app/brand-logos/vfd/bosch-rexroth.png",
  honeywell: "/faults-app/brand-logos/vfd/honeywell.png",
  emerson: "/faults-app/brand-logos/vfd/emerson.png",
};

const plcCompanyLogoScale: Record<string, number> = {
  siemens: 2.04,
  rockwell: 2.0,
  mitsubishi: 2.04,
  schneider: 2.04,
  omron: 2.04,
  abb: 2.0,
  delta: 2.0,
  beckhoff: 2.12,
  wago: 1.8,
  "phoenix-contact": 1.92,
  unitronics: 2.24,
  keyence: 2.24,
  panasonic: 2.04,
  "ls-electric": 2.04,
  automationdirect: 2.04,
  "br-automation": 2.04,
  "bosch-rexroth": 2.04,
  honeywell: 2.04,
  emerson: 2.04,
};

function ManufacturerGrid({
  manufacturers,
  onSelect,
}: {
  manufacturers: ReturnType<
    typeof fetchPlcManufacturers
  >;
  onSelect: (id: string) => void;
}) {
  if (manufacturers.length === 0) {
    return (
      <EmptyPlcState text="ماكو شركة PLC مطابقة للبحث." />
    );
  }

  return (
    <>
      <div className="mb-3 flex items-center gap-1.5 sm:gap-2">
        <Building2 className="h-3.5 w-3.5 text-primary sm:h-4 sm:w-4" />
        <h2 className="font-extrabold">
          اختار الشركة المصنعة
        </h2>
        <span className="text-xs text-muted-foreground">
          ({manufacturers.length})
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-3">
        {manufacturers.map((item) => {
          const logo = plcCompanyLogos[item.id];
          const logoScale = plcCompanyLogoScale[item.id] ?? 1;

          return (
            <button
              key={item.id}
              type="button"
              onClick={() =>
                onSelect(item.id)
              }
              className="card-tech press flex min-h-28 flex-col items-center justify-center p-2 text-center transition hover:border-primary sm:min-h-32 sm:p-2.5"
            >
              {logo ? (
                <div className="flex h-12 w-full max-w-[120px] items-center justify-center rounded-lg bg-white px-1.5 py-1.5 sm:h-14 sm:max-w-[155px]">
                  <img
                    src={logo}
                    alt={item.name}
                    loading="lazy"
                    className="h-full w-full object-contain"
                    style={{
                      transform: `scale(${logoScale})`,
                      transformOrigin: "center",
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              ) : (
                <>
                  <span className="grid h-7 w-7 place-items-center rounded-md bg-accent text-accent-foreground sm:h-8 sm:w-8">
                    <Building2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  </span>

                  <h3 className="mt-1 text-[11px] font-extrabold sm:text-xs">
                    {item.name}
                  </h3>
                </>
              )}

              <p className="mt-1 text-[9px] font-bold text-primary sm:text-[10px]">
                {item.series.length} سلسلة
              </p>
            </button>
          );
        })}
      </div>
    </>
  );
}

function SeriesGrid({
  manufacturerId,
  series,
  onSelect,
}: {
  manufacturerId: string;
  series: ReturnType<
    typeof fetchPlcSeries
  >;
  onSelect: (id: string) => void;
}) {
  if (series.length === 0) {
    return (
      <EmptyPlcState text="ماكو سلسلة PLC مطابقة للبحث." />
    );
  }

  return (
    <>
      <div className="mb-3 flex items-center gap-2">
        <Layers3 className="h-5 w-5 text-primary" />
        <h2 className="font-extrabold">
          اختار السلسلة
        </h2>
        <span className="text-xs text-muted-foreground">
          ({series.length})
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {series.map((item) => {
          const models = fetchPlcModels(
            manufacturerId,
            item.id,
          );

          return (
            <button
              key={item.id}
              type="button"
              onClick={() =>
                onSelect(item.id)
              }
              className="card-tech press p-4 text-start transition hover:border-primary"
            >
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-accent text-accent-foreground">
                <Layers3 className="h-5 w-5" />
              </span>

              <h3 className="mt-3 font-extrabold">
                {item.name}
              </h3>

              {(item.engineeringSoftware ?? []).length >
                0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {(item.engineeringSoftware ?? []).join(
                    " • ",
                  )}
                </p>
              )}

              <p className="mt-3 text-xs font-bold text-primary">
                {models.length} موديل / CPU
              </p>
            </button>
          );
        })}
      </div>
    </>
  );
}

function ModelGrid({
  manufacturerId,
  seriesId,
  models,
  onSelect,
}: {
  manufacturerId: string;
  seriesId: string;
  models: string[];
  onSelect: (model: string) => void;
}) {
  if (models.length === 0) {
    return (
      <EmptyPlcState text="ماكو موديل PLC مطابق للبحث." />
    );
  }

  return (
    <>
      <div className="mb-3 flex items-center gap-2">
        <Cpu className="h-5 w-5 text-primary" />
        <h2 className="font-extrabold">
          اختار الموديل / CPU
        </h2>
        <span className="text-xs text-muted-foreground">
          ({models.length})
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {models.map((model) => {
          const modelFaults =
            fetchPlcFaults({
              manufacturerId,
              seriesId,
              model,
            });

          const modelCodes =
            fetchPlcDiagnosticCodes({
              manufacturerId,
              seriesId,
              model,
            });

          return (
            <button
              key={model}
              type="button"
              onClick={() =>
                onSelect(model)
              }
              className="card-tech press p-4 text-start transition hover:border-primary"
            >
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-accent text-accent-foreground">
                <Cpu className="h-5 w-5" />
              </span>

              <h3 className="mt-3 break-words font-extrabold">
                {model}
              </h3>

              <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
                <span className="rounded-full bg-muted px-2 py-1">
                  {modelFaults.length} عطل
                </span>

                <span className="rounded-full bg-muted px-2 py-1">
                  {modelCodes.length} كود تشخيصي
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

function PlcModelDetails({
  manufacturerId,
  seriesId,
  model,
  q,
}: {
  manufacturerId: string;
  seriesId: string;
  model: string;
  q: string;
}) {
  const normalizedQuery = normalize(
    q.trim(),
  );

  const matches = (
    values: string[],
  ) => {
    if (!normalizedQuery) {
      return true;
    }

    const terms = normalizedQuery
      .split(/[\s،.,؟?/_-]+/)
      .filter(Boolean);

    const hay = normalize(
      values.join(" "),
    );

    return terms.every((term) =>
      hay.includes(term),
    );
  };

  const faults = fetchPlcFaults({
    manufacturerId,
    seriesId,
    model,
  }).filter((fault) =>
    matches([
      fault.title_ar,
      fault.description_ar,
      fault.model,
      fault.code ?? "",
      ...fault.symptoms,
      ...fault.causes.map(
        (cause) => cause.cause,
      ),
      ...fault.solutions,
      ...fault.keywords,
    ]),
  );

  const codes =
    fetchPlcDiagnosticCodes({
      manufacturerId,
      seriesId,
      model,
    }).filter((item) =>
      matches([
        item.code,
        item.code_type,
        item.title_ar,
        item.description_ar,
        ...item.symptoms,
        ...item.causes,
        ...item.solutions,
        ...item.keywords,
      ]),
    );

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-primary/25 bg-accent/40 p-4">
        <div className="flex items-center gap-2">
          <Cpu className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-extrabold">
            {model}
          </h2>
        </div>

        <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
          <span className="rounded-full bg-card px-3 py-1.5">
            {faults.length} عطل
          </span>

          <span className="rounded-full bg-card px-3 py-1.5">
            {codes.length} كود تشخيصي
          </span>
        </div>
      </div>

      <div>
        <div className="mb-3 flex items-center gap-2">
          <Wrench className="h-5 w-5 text-primary" />
          <h3 className="font-extrabold">
            الأعطال
          </h3>
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          {faults.map((fault) => (
            <article
              key={fault.id}
              className="card-tech p-4"
            >
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="font-extrabold">
                  {fault.title_ar}
                </h4>

                {fault.code && (
                  <span className="rounded bg-steel px-2 py-0.5 text-xs font-bold text-steel-foreground">
                    {fault.code}
                  </span>
                )}
              </div>

              <p className="mt-2 text-sm text-muted-foreground">
                {fault.description_ar}
              </p>

              {fault.symptoms.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-extrabold">
                    الأعراض:
                  </p>

                  <ul className="mt-1 space-y-1 text-sm text-muted-foreground">
                    {fault.symptoms
                      .slice(0, 3)
                      .map((item, index) => (
                        <li
                          key={index}
                          className="flex gap-2"
                        >
                          <span className="text-primary">
                            •
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}

              {fault.causes.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-extrabold">
                    الأسباب المحتملة:
                  </p>

                  <ul className="mt-1 space-y-1 text-sm text-muted-foreground">
                    {fault.causes
                      .slice(0, 3)
                      .map((item, index) => (
                        <li
                          key={index}
                          className="flex gap-2"
                        >
                          <span className="text-primary">
                            •
                          </span>
                          <span>
                            {item.cause}
                          </span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}

              {fault.tests.length > 0 && (
                <div className="mt-3 rounded-xl bg-muted/60 p-3">
                  <p className="text-xs font-extrabold">
                    أول فحص:
                  </p>

                  <p className="mt-1 text-sm text-muted-foreground">
                    {fault.tests[0].method}
                  </p>
                </div>
              )}

              {fault.solutions.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-extrabold">
                    الحل:
                  </p>

                  <p className="mt-1 text-sm text-muted-foreground">
                    {fault.solutions[0]}
                  </p>
                </div>
              )}

              <div className="mt-3">
                <StatusBadge
                  tone={severityToneKey(
                    fault.severity,
                  )}
                >
                  درجة الخطورة:
                  {" "}
                  {fault.severity}
                </StatusBadge>
              </div>
            </article>
          ))}

          {faults.length === 0 && (
            <EmptyPlcState text="ماكو أعطال مطابقة لهذا الموديل والبحث الحالي." />
          )}
        </div>
      </div>

      <div>
        <div className="mb-3 flex items-center gap-2">
          <Stethoscope className="h-5 w-5 text-primary" />
          <h3 className="font-extrabold">
            الأكواد والحالات التشخيصية
          </h3>
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          {codes.map((item) => (
            <article
              key={item.id}
              className="card-tech p-4"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-lg bg-primary px-2.5 py-1 text-sm font-black text-primary-foreground">
                  {item.code}
                </span>

                <span className="rounded-lg bg-muted px-2 py-1 text-xs font-bold">
                  {item.code_type}
                </span>
              </div>

              <h4 className="mt-3 font-extrabold">
                {item.title_ar}
              </h4>

              <p className="mt-2 text-sm text-muted-foreground">
                {item.description_ar}
              </p>

              {item.causes.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-extrabold">
                    الأسباب:
                  </p>

                  <ul className="mt-1 space-y-1 text-sm text-muted-foreground">
                    {item.causes
                      .slice(0, 3)
                      .map((cause, index) => (
                        <li
                          key={index}
                          className="flex gap-2"
                        >
                          <span className="text-primary">
                            •
                          </span>
                          <span>{cause}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}

              {item.checks.length > 0 && (
                <div className="mt-3 rounded-xl bg-muted/60 p-3">
                  <p className="text-xs font-extrabold">
                    أول خطوة فحص:
                  </p>

                  <p className="mt-1 text-sm text-muted-foreground">
                    {item.checks[0].action}
                  </p>
                </div>
              )}

              {item.solutions.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-extrabold">
                    الحل:
                  </p>

                  <p className="mt-1 text-sm text-muted-foreground">
                    {item.solutions[0]}
                  </p>
                </div>
              )}

              <div className="mt-3">
                <StatusBadge
                  tone={severityToneKey(
                    item.severity,
                  )}
                >
                  درجة الخطورة:
                  {" "}
                  {item.severity}
                </StatusBadge>
              </div>
            </article>
          ))}

          {codes.length === 0 && (
            <EmptyPlcState text="ماكو أكواد تشخيصية مطابقة لهذا الموديل والبحث الحالي." />
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyPlcState({
  text,
}: {
  text: string;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
      {text}
    </div>
  );
}
